import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useRouter } from 'next/router';
import Head from 'next/head';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import * as Sentry from '@sentry/node';
import {
  FrequentlyBoughtTogether,
  RelatedProducts
} from '@algolia/recommend-react';
import recommend from '@algolia/recommend';

import BuildUrl from 'build-url';
import { logAmplitude } from '../../utils/amplitude';
// algolia search client
import searchClient from '../../utils/algolia';

import {
  createRecentlyViewed,
  getRecentlyViewed,
  getProduct as fetchProduct
} from '../api';

import { toggleCartModalVisibility } from '../../store/modules/interfaces/actions';

import { generateMeta, generateBreadcrumbSeoSchema } from '../../utils/meta';
import isMobile from '../../utils/isMobile';
import { Page, Content } from '../../theme/page';
import { trackPageType, trackProductView } from '../../utils/tracking';
import getCookie from '../../utils/getCookie';
import hasError from '../../utils/hasError';
import SCOPE_TYPES from '../../utils/sentryScopeTypes';
import getCanonicalUrl from '../../utils/getCanonicalUrl';

import { Skeleton, SkeletonWrapper } from '../../atoms/skeleton';
import BreadCrumbs from '../../molecules/breadcrumbs';
import ErrorPage from '../_error';
import ProductCardCarousel from '../../organs/product-card-carousel';
import ProductCardCarouselWrapper from '../../organs/product-algolia-card-carousel';
import ProductCarousel from '../../tissues/product-carousel';
import ProductDetails from '../../organs/pdp-details';
import ProductAddedConfirmation from '../../tissues/product-confirm-cart';
import MobileBottomSheet from '../../tissues/mobile-bottomsheet';
import approvedBrands from './brands.json';

// TODO: remove this and pdpVariants cookie once dynamic data is ready
// import newProductData from '../product-new/new_pdp_view.json';

// Context
import { useProduct } from '../../utils/context/product-provider';

const DetailsSkeletonWrapper = styled(SkeletonWrapper)`
  grid-area: product-information;
`;

const CarouselSkeletonWrapper = styled(SkeletonWrapper)`
display: flex;
  grid-area: product-carousel;

  ${Skeleton}::before {
    content: "";
    display: block;
    padding-bottom: 100%;
  }
`;

const BreadcrumbSkeletonWrapper = styled(SkeletonWrapper)`
  grid-area: breadcrumbs;

  ${Skeleton} {
    height: 1.5rem;
    margin-bottom: 3rem;
  }
`;

const DetailsSkeleton = () => (
  <DetailsSkeletonWrapper>
    {/* Product Header */}
    <Skeleton css={{ height: '12rem', marginBottom: '3rem' }} />

    {/* Product Information */}
    <Skeleton css={{ height: '35rem', marginBottom: '3rem' }} />

    {/* Product Options */}
    <Skeleton css={{ height: '30rem', marginBottom: '3rem' }} />

    {/* Product Options */}
    <Skeleton css={{ height: '7rem', marginBottom: '3rem' }} />
  </DetailsSkeletonWrapper>
);

const CarouselSkeleton = () => (
  <CarouselSkeletonWrapper>
    <Skeleton />
  </CarouselSkeletonWrapper>
);

const BreadcrumbSkeleton = () => (
  <BreadcrumbSkeletonWrapper>
    <Skeleton />
  </BreadcrumbSkeletonWrapper>
);

const Product = styled.div`
  display: grid;
  grid-gap: 3rem;
  grid-template-areas: 'breadcrumbs' 'product-carousel' 'product-information';
  grid-template-columns: minmax(0, 1fr);
  margin-bottom: 3rem;

  @media (min-width: ${(props) => props.theme.breakpoint.medium}) {
    grid-gap: 5rem;
    grid-template-columns: 50%;
    grid-template-areas: 'breadcrumbs breadcrumbs' 'product-carousel product-information';
  }
`;

const getMetaTitle = (name, brand = '', category = '') => {
  let title = name;

  if (brand) {
    title += ` - ${brand}`;
    if (category) title += ` ${category}`;
  } else if (category) {
    title += ` - ${category}`;
  }

  title += ' | Maisonette';

  return title;
};

const getMetaDescription = (name, brand = '', category = '') => {
  let description = `Discover the ${name}`;

  if (brand) {
    description += ` from ${brand}.`;
    if (category) description += ` Shop ${category} and more from Maisonette's curated selection.`;
  } else if (category) {
    description += `. Shop ${category} and more from Maisonette's curated selection.`;
  }

  return description;
};

const getMetaDetails = (product) => {
  const { name, brand = '', breadcrumb_taxons = [] } = product;
  const category = breadcrumb_taxons[breadcrumb_taxons?.length - 1]?.name ?? null;

  return {
    title: getMetaTitle(name, brand, category),
    description: getMetaDescription(name, brand, category)
  };
};

const ProductDetailPage = (props) => {
  const index = searchClient.initIndex(process.env.NEXT_PUBLIC_ALGOLIA_PRODUCTS_INDEX);
  const router = useRouter();
  const productMeta = getMetaDetails(props.product);

  const getAvailability = (variantsArray = []) => {
    if (!variantsArray?.length) return 'https://schema.org/OutOfStock';

    return variantsArray.some(({ in_stock = false }) => in_stock)
      ? 'https://schema.org/InStock'
      : 'https://schema.org/OutOfStock';
  };

  const getPrice = (variantsArray) => {
    let price = 0;

    if (!variantsArray?.length) return price;

    for (let i = 0; i <= variantsArray.length - 1; i++) {
      const variant = variantsArray[i];
      if (variant.price) {
        price = variant.price;
        break;
      }
    }

    if (price) {
      const [whole, fraction] = price.toString().split('.');
      if (+fraction === 0) return whole;
      return price;
    }

    return price;
  };

  const meta = {
    key: 'product',
    title: productMeta.title,
    description: productMeta.description,
    viewport: {
      content: 'width=device-width,initial-scale=1,maximum-scale=10.0,user-scalable=yes',
      name: 'viewport'
    },
    og: {
      description: productMeta.description,
      image: props.product?.master?.images?.length > 0
        ? props.product?.master?.images[0]?.large_url ?? null
        : null,
      title: productMeta.title,
      url: `${process.env.NEXT_PUBLIC_CLIENT_HOST}${router?.asPath}`
    },
    twitter: {
      description: productMeta.description,
      image: props.product?.master?.images?.length > 0
        ? props.product?.master?.images[0]?.large_url ?? null
        : null,
      title: productMeta.title
    }
  };

  const createNewScript = (variantObj, colorVariantsArray) => {
    // for initial render and default script data
    const newScriptData = {
      image: `${props?.product?.master?.images[0]?.large_url}`,
      description: `${props?.product?.description}`,
      price: `${getPrice(props.product?.variants) ?? props?.product?.master?.price}`,
      availability: `${getAvailability(props.product?.variants)}`
    };

    // if the variantObj is truthy,
    // this means the user changed the color
    if (variantObj) {
      newScriptData.image = `${variantObj.images.find((image) => (image.position === 1))?.large_url
        ?? variantObj.images[0].large_url}`;
      newScriptData.description = `${variantObj.description ?? newScriptData.description}`;
      newScriptData.price = `${colorVariantsArray.length ? getPrice(colorVariantsArray) : newScriptData.price}`;
      newScriptData.availability = `${colorVariantsArray.length ? getAvailability(colorVariantsArray) : newScriptData.availability}`;
    }

    return {
      __html: `
        {
          "@context": "https://schema.org/",
          "@type": "Product",
          "name": "${props.product.name}",
          "image": "${newScriptData.image}",
          "description": "${newScriptData.description}",
          "brand": "${props?.product?.brand}",
          "sku": "${props?.product?.master?.sku}",
          "offers": {
            "@type": "Offer",
            "url": "${process.env.NEXT_PUBLIC_CLIENT_HOST}/product/${props?.product?.slug}",
            "priceCurrency": "USD",
            "price": "${newScriptData.price}",
            "availability": "${newScriptData.availability}",
            "itemCondition": "https://schema.org/NewCondition"
          }
        }
      `
    };
  };

  // eslint-disable-next-line max-len
  const [currentScript, setCurrentScript] = useState(createNewScript(null, props.product?.variants));
  const [currentMeta, setCurrentMeta] = useState(meta);
  const [recentlyViewed, setRecentlyViewed] = useState(false);

  /**
   * get pdpVariants value from siteSpect
   * which is pulled out and replaced with static value for a while
   */
  const pdpVariants = false;
  const {
    state: {
      activeColor,
      activeColorVariants,
      productVariantAdded,
      productAddedModalActive
    },
    setProductAddedModalActive
  } = useProduct();

  useEffect(() => {
    global.window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (pdpVariants && activeColor.length) {
      const { maisonette_variant_group_attributes: variant_attributes } = props.product;

      const validColor = variant_attributes.find((v) =>
        (v?.option_value?.name).toLowerCase() === activeColor);

      if (validColor) {
        setCurrentScript(createNewScript(validColor, activeColorVariants));

        if (validColor.images?.length) {
          const newMeta = currentMeta;

          const newImage = validColor.images.find((image) => (image.position === 1))?.large_url
            ?? validColor.images[0].large_url;

          newMeta.og.image = newImage;
          newMeta.twitter.image = newImage;

          setCurrentMeta(newMeta);
        }
      }
    }
  }, [activeColor]);

  const recommendClient = recommend(
    process.env.NEXT_PUBLIC_ALGOLIA_APP_ID,
    process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_API_KEY
  );

  const setProductAsRecentlyViewed = async (variant_id) => {
    const session_id = getCookie('maisonette_session_token');

    // TODO: Assess this stopgap / guard clause against no session_id
    // An ideal flow here may be to create a session_id if one does not exist first
    if (!session_id) {
      setRecentlyViewed(false);
      return;
    }

    await createRecentlyViewed({
      body: {
        recently_viewed: {
          session_id,
          variant_id
        }
      }
    });
  };

  const getRecentlyViewedProducts = () => {
    const session_id = getCookie('maisonette_session_token');

    // TODO: Assess this stopgap / guard clause against no session_id
    if (!session_id) {
      setRecentlyViewed(false);
      return;
    }

    // Assuming a session_id, get the recently viewed products
    getRecentlyViewed({ session_id })
      .then(async (res) => {
        if (hasError(res)) {
          setRecentlyViewed(false);
          return;
        }

        try {
          const ids = res
            // converting to string as getObjects method accept string array
            .map((x) => x.variant_id.toString())
            // remove current product from ids
            .filter((id) => Number(id) !== props.product.master.id)
            .slice(0, 20);

          if (ids) {
            /**
             * getObjects lets you retrieve multiple objects from a specified index
             * For more details, please check: https://www.algolia.com/doc/api-reference/api-methods/get-objects
             */
            index.getObjects(ids).then(({ results }) => {
              // filter to remove the null record
              setRecentlyViewed(results.filter((result) => result));
            });
          }
        } catch (error) {
          setRecentlyViewed(false);
        }
      });
  };

  useEffect(() => {
    if (props.product && !hasError(props.product)) {
      // trackPageType is here to ensure that the GA page view event
      // happens before the product view event
      // TODO: Address these calls when updating the google analytics system
      trackPageType('Product Description Pages', router.asPath);
      trackProductView({ product: props.product });
      setProductAsRecentlyViewed(props.product.master.id);
      getRecentlyViewedProducts();
    }
  }, [props.product]);

  const getProductType = () => {
    const { classifications } = props.product;
    const [productType] = classifications.filter((c) => {
      const prettyName = c.taxon?.pretty_name;
      return prettyName.split('->')?.[0]?.trim() === 'Product Type';
    });
    return productType?.taxon?.name;
  };

  if (router?.isFallback) {
    return (
      <Page id="maincontent">
        <Content>
          <Product>
            <BreadcrumbSkeleton />
            <CarouselSkeleton />
            <DetailsSkeleton />
          </Product>
        </Content>
      </Page>
    );
  }

  if (!props.product || hasError(props.product)) {
    return <ErrorPage statusCode={404} />;
  }

  const hasImages = props.product?.master && props.product?.master?.images;

  const getProductVariantAdded = (addedVariantId) => {
    const variantDetails = props.product?.variants.filter((v) => v.id === addedVariantId)[0];
    return variantDetails;
  };

  const getQuantityInBag = (addedVariantId) => {
    const itemInCart = props.lineItems?.filter((l) => l.variant_id === addedVariantId)[0];
    return itemInCart?.quantity;
  };

  const closeAddedtoBagModal = () => setProductAddedModalActive(false);

  const onCloseModal = () => {
    closeAddedtoBagModal();
    logAmplitude('Add to Cart Confirmation', {
      product: props.product,
      isViewed: false,
      isContinue: true
    });
  };

  return (
    <div key={props.product.id}>
      <Head>
        {generateMeta(currentMeta)}

        <link key="canonical" rel="canonical" href={getCanonicalUrl(router)} />
        <link
          rel="preload"
          as="image"
          imagesrcset={`${props.product?.master?.images[0]?.product_url} 400w, ${props.product?.master?.images[0]?.product_large_url} 993w`}
          imagesizes="(max-width: 992px) 400w, 993px"
          fetchpriority="high"
        />

        {generateBreadcrumbSeoSchema(props)}

        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={currentScript}
        />
      </Head>

      <Page id="maincontent">
        <Content>
          <Product>
            <BreadCrumbs breadcrumbs={props.product?.breadcrumb_taxons || []} />

            {
              hasImages && (
                <ProductCarousel
                  product={props.product}
                  images={props.product?.master?.images}
                  name={props.product?.master?.name}
                  productType={getProductType()}
                />
              )
            }

            <ProductDetails product={props.product} />
          </Product>

          {
            approvedBrands[props.product?.brand] && (
              <ProductCardCarouselWrapper
                brand={props.product?.brand}
                title="More From This Brand"
                id="more-from-this-brand"
                productName={props.product?.name}
                productID={props.product?.id}
              />
            )
          }
          {props?.hasRecommendations
            ? (
              <RelatedProducts
                recommendClient={recommendClient}
                indexName={process.env.NEXT_PUBLIC_ALGOLIA_PRODUCTS_INDEX}
                objectIDs={[`${props.product?.master?.id}`]}
                fallbackComponent={() => (
                  <ProductCardCarouselWrapper
                    brand={props.product?.brand}
                    title="Related Products"
                    id="related-products"
                    productName={props.product?.name}
                    productBreadcrumbs={props.product?.breadcrumb_taxons}
                    productID={props.product?.id}
                  />
                )}
                view={({ items }) => (
                  <ProductCardCarousel
                    title="Related Products"
                    products={items}
                    id="related-products"
                    isInfinite
                  />
                )}
                // eslint-disable-next-line react/no-children-prop
                children={
                  ({ View, Fallback, recommendations }) => (
                    recommendations.length
                      ? <View />
                      : <Fallback />
                  )
                }

              />
            )
            : null}

          {(props?.hasRecommendations && !approvedBrands[props.product?.brand])
            ? (
              <FrequentlyBoughtTogether
                recommendClient={recommendClient}
                indexName={process.env.NEXT_PUBLIC_ALGOLIA_PRODUCTS_INDEX}
                objectIDs={[`${props.product?.master?.id}`]}
                // https://www.algolia.com/doc/ui-libraries/recommend/api-reference/recommend-react/FrequentlyBoughtTogether/#param-fallbackcomponent
                fallbackComponent={() => (
                  <ProductCardCarouselWrapper
                    brand={props.product?.brand}
                    title="Also Bought With"
                    id="also-bought-with"
                    productName={props.product?.name}
                    productBreadcrumbs={props.product?.breadcrumb_taxons}
                    productID={props.product?.id}
                  />
                )}
                view={({ items }) => (
                  <ProductCardCarousel
                    title="Also Bought With"
                    products={items}
                    id="also-bought-with"
                    isInfinite
                  />
                )}
                // eslint-disable-next-line react/no-children-prop
                children={
                  ({ View, Fallback, recommendations }) => (
                    recommendations.length
                      ? <View />
                      : <Fallback />
                  )
                }
              />
            )
            : null}
          {
            recentlyViewed && (
              <ProductCardCarousel
                title="Recently Viewed"
                products={recentlyViewed}
                id="recently-viewed"
              />
            )
          }
        </Content>
      </Page>
      {isMobile() && (
        <MobileBottomSheet
          isOpen={productAddedModalActive}
          onClose={onCloseModal}
        >
          <ProductAddedConfirmation
            variant={getProductVariantAdded(productVariantAdded)}
            brand={props.product?.brand}
            quantity={getQuantityInBag(productVariantAdded)}
            closeModal={closeAddedtoBagModal}
            toggleCart={props.toggleCart}
            product={props.product}
          />
        </MobileBottomSheet>
      )}
    </div>
  );
};

export async function getServerSideProps({ query }) {
  const { slug, ...params } = query;

  // if there is a 'quantity' param,
  // redirect to the PDP URL without it
  // to avoid indexing the query param in Google
  if (params.quantity) {
    delete params.quantity;

    const url = params && Object.keys(params).length
      ? BuildUrl(`/product/${slug}`, { queryParams: params })
      : `/product/${slug}`;

    return {
      redirect: {
        destination: url,
        permanent: true
      }
    };
  }

  // get cookie value by SiteSpect which is pulled out and replaced with false value for a while
  // const pdpRevamp = req.cookies.sitespect_pdpRevamp;
  const pdpRevamp = false;

  if (pdpRevamp) {
    // redirect to new PDP if in treatment

    // add query params in url
    const url = params && Object.keys(params).length
      ? BuildUrl(`/product-new/${slug}`, { queryParams: params })
      : `/product-new/${slug}`;

    return {
      redirect: {
        destination: url,
        permanent: false
      }
    };
  }

  const getProduct = (id) => fetchProduct({
    uri: `/api/products/${id}`,
    scopes: [SCOPE_TYPES.SERVICES.SOLIDUS],
    id
  });

  // if there was an error fetching product
  // - return null for product,
  // - revalidation will try to fetch again on next attempt
  const product = await getProduct(slug)
    .catch((error) => {
      Sentry.withScope((scope) => {
        scope.setContext('slug', slug);
        scope.setFingerprint('/pages/product/[slug]::getServerSideProps');
        Sentry.captureException(error);
      });

      return null;
    });
  if (product && hasError(product)) return { notFound: true };

  /* eslint-disable no-confusing-arrow, no-unused-vars */
  // TODO: Consider a better way to do this; we currently remove OOS products from the index
  //       which prevents the RelatedProducts/FrequentlyBoughtTogether from working.
  // We're specifically trying to `catch()` where: err.message === 'ObjectID does not exist'
  const searchIndex = searchClient.initIndex(process.env.NEXT_PUBLIC_ALGOLIA_PRODUCTS_INDEX);
  const hasRecommendations = await searchIndex
    .getObject(product?.master?.id)
    .then((onSuccess) => true)
    .catch((err) => false);
  /* eslint-enable no-confusing-arrow, no-unused-vars */

  return {
    // TODO comment this back after BE PDP variant work is completed:
    props: { product, hasRecommendations }
    // props: { product: newProductData }
  };
}

const mapStateToProps = (state) => ({
  lineItems: state.cart.line_items
});

const mapDispatchToProps = (dispatch) => ({
  toggleCart: (isActive) => dispatch(toggleCartModalVisibility(isActive))
});

ProductDetailPage.defaultProps = {
  product: null,
  toggleCart: () => { },
  lineItems: null
};

ProductDetailPage.propTypes = {
  product: PropTypes.object,
  toggleCart: PropTypes.func,
  lineItems: PropTypes.array
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductDetailPage);
